import { type KeyboardNavigationMode } from "@common/hocs/keyboardNavigation";
import { doesOptionMatchText } from "@common/lib/options";
import { filterNodes } from "@common/lib/tree";
import { Common } from "@common/types";
import { identity, isArray } from "lodash";
import React from "react";
import Search from "../../Search/component";
import { type SelectHeading } from "../Base/component";
import { type ValueType } from "../component";

export type WithSearchComponentProps = {
  allowToggleEmpty?: boolean;
  heading?: SelectHeading | SelectHeading[];
  keyboardNavigationMode?: KeyboardNavigationMode;
  searchText?: string;
};

export type WithSearchHOCBaseProps = {
  onChangeDelay?: number;
  searchPlaceholder?: string;
  searchOnEnter?: (text: string, value?: ValueType) => void | Promise<void>;
  searchTourStep?: string;
};
export const withSearchHOC =
  <TProps extends WithSearchComponentProps>(
    Component: React.ComponentType<TProps>,
    onChangeDelay: number = 300
  ): React.FunctionComponent<TProps & WithSearchHOCBaseProps> =>
  (props: TProps & WithSearchHOCBaseProps) => {
    const [isBusy, setIsBusy] = React.useState(false);
    const [searchText, setSearchText] = React.useState("");

    const { searchPlaceholder, searchOnEnter, ...cmpProps } = props;

    const onEnter = React.useCallback(async () => {
      const res = searchText && searchOnEnter && searchOnEnter(searchText);
      if (res instanceof Promise) {
        setIsBusy(true);
        try {
          await res;
        } finally {
          setIsBusy(false);
        }
      }
    }, [searchOnEnter, searchText]);

    const heading = React.useMemo((): SelectHeading[] => {
      const heading = props.heading
        ? isArray(props.heading)
          ? (props.heading.slice() as SelectHeading[])
          : ([props.heading] as SelectHeading[])
        : [];

      heading.splice(0, 0, {
        content: (
          <Search
            // autoFocus makes everything jump on mobile. TODO: figure out the root cause and fix
            // autoFocus
            tourStep={props.searchTourStep}
            onChange={isBusy ? identity : setSearchText}
            onEnter={isBusy ? identity : onEnter}
            isBusy={isBusy}
            placeholder={props.searchPlaceholder}
            onChangeTrigger={props.onChangeDelay ?? onChangeDelay}
            value={searchText}
          />
        ),
        isSearch: true,
        isToggle: false
      });

      return heading;
    }, [isBusy, props.heading, props.onChangeDelay, searchPlaceholder, searchText, setSearchText]);

    return (
      <Component {...(cmpProps as TProps)} heading={heading} keyboardNavigationMode="arrows" searchText={searchText} />
    );
  };

type ComponentProps = Common.WithOptions<Common.OptionItem<ValueType>> & {
  allOptions: Common.OptionItem<ValueType>[];
  hint?: any;
  heading?: SelectHeading | SelectHeading[];
};

export type WithSearchHOCProps = WithSearchHOCBaseProps & {
  searchNoExactMatchHint?: string;
};

const withSearchTextHOC =
  <TProps extends ComponentProps>(Component: React.ComponentType<TProps>) =>
  ({
    searchText,
    searchNoExactMatchHint,
    heading,
    ...props
  }: Omit<TProps, "allOptions"> & {
    searchText?: string;
  } & WithSearchHOCProps) => {
    const options = React.useMemo(
      () => filterNodes(props.options, (node) => doesOptionMatchText(node, searchText), "child"),
      [props.options, searchText]
    );

    const exactMatch = React.useMemo(
      () =>
        options.find(
          (x) =>
            x.label?.toLowerCase() === (searchText ?? "").toLowerCase() ||
            x.descriptions?.some((y) => (y.label ?? "").toLowerCase() === (searchText ?? "").toLowerCase()) ||
            x.group?.toLowerCase() === (searchText ?? "").toLowerCase()
        ),
      [options, searchText]
    );

    const _heading = React.useMemo((): SelectHeading[] | undefined => {
      if (!heading) return;

      let result = isArray(heading) ? (heading.slice() as SelectHeading[]) : ([heading] as SelectHeading[]);

      return result.map((x) => ({
        ...x,
        hint: x.isSearch && searchText && !exactMatch ? searchNoExactMatchHint : x.hint
      }));
    }, [exactMatch, heading, searchNoExactMatchHint, searchText]);

    return <Component {...(props as TProps)} allOptions={props.options} heading={_heading} options={options} />;
  };
export default withSearchTextHOC;
